import React, { useState, useRef, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkle, PaperPlaneRight, User, Robot } from '@phosphor-icons/react';
import { ScrollArea } from '@/components/ui/scroll-area';

const SUGGESTED_TOPICS = [
  { title: 'Where do I start?', query: 'Where do I start as a new user? Walk me through the recommended workflow.' },
  { title: 'How does Wealth Health Score™ work?', query: 'How does the Wealth Health Score™ work and what do the 5 dimensions mean?' },
  { title: 'Show me Retirement Gap Analyzer', query: 'How do I use the Retirement Income Gap Analyzer and what do the closure scenarios mean?' },
  { title: 'Compare Roth + Social Security', query: 'How do the Roth Conversion Analyzer and Social Security Optimization Engine work together?' },
  { title: 'Budgeting & Spending', query: 'Explain budgeting basics and effective spending strategies' },
  { title: 'Savings Strategies', query: 'What are the best savings strategies for different financial goals?' },
  { title: 'Inflation Impacts', query: 'How does inflation affect my purchasing power and retirement planning?' },
  { title: 'Retirement Planning', query: 'Explain retirement planning basics including 401(k) and IRA accounts' },
  { title: 'Roth Conversions', query: 'What are Roth conversions and when might they be beneficial?' },
  { title: 'RMD Planning', query: 'Explain Required Minimum Distributions and how to plan for them' },
  { title: 'Social Security Tax', query: 'How is Social Security income taxed?' },
  { title: 'Tax-Aware Withdrawals', query: 'Explain tax-aware withdrawal strategies for retirement accounts' },
];

const AIFinancialCoach = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Welcome! I'm your PLANIVANCE AI Coach. I can help you navigate the platform AND understand financial concepts like budgeting, retirement planning, and tax strategies.

Try asking:
• "Where do I start?"
• "How does the Wealth Health Score™ work?"
• "Show me how to use the Retirement Gap Analyzer"

**Important:** I provide general financial education and navigation guidance only — not personalized advice. For your specific situation, book a consultation via Dashboard → Getting Started → Book a Consultation.

What would you like to explore?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e, customQuery = null) => {
    e?.preventDefault();
    const query = customQuery || input;
    
    if (!query.trim()) return;

    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/ai/query', { question: query });
      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I\'m temporarily unavailable. Please try again in a moment.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (query) => {
    handleSubmit(null, query);
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header with Disclaimer */}
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={24} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="cabinet-grotesk text-lg font-medium text-[#0A1128] mb-2">Educational AI Financial Coach</h2>
            <div className="text-sm text-[#4A5568] space-y-1">
              <p><strong className="text-[#1A202C]">Educational Use Only:</strong> This AI provides general financial education and concept explanations. It does NOT provide personalized financial, investment, tax, or legal advice.</p>
              <p><strong className="text-[#1A202C]">Professional Consultation Required:</strong> Always consult with qualified financial advisors, tax professionals, and legal counsel for advice tailored to your specific situation.</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          AI Financial Coach
        </h1>
        <p className="text-base text-[#4A5568]">
          Learn about financial concepts and strategies
        </p>
      </div>

      {/* Suggested Topics */}
      {messages.length <= 1 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="suggested-topics">
          <h3 className="cabinet-grotesk text-lg font-medium text-[#0A1128] mb-4">Suggested Topics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SUGGESTED_TOPICS.map((topic, idx) => (
              <button
                key={idx}
                onClick={() => handleTopicClick(topic.query)}
                data-testid={`topic-${topic.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-left p-3 border border-slate-200 rounded-md hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors"
              >
                <p className="text-sm font-medium text-[#0A1128]">{topic.title}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid="ai-chat-area">
        <div 
          ref={scrollRef}
          className="h-[500px] overflow-y-auto p-6 space-y-4"
        >
          {messages.map((message, idx) => (
            <div
              key={idx}
              data-testid={`message-${message.role}-${idx}`}
              className={`flex gap-3 ${
                message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'user' 
                  ? 'bg-[#0A1128]' 
                  : 'bg-[#C5A059]'
              }`}>
                {message.role === 'user' ? (
                  <User size={18} weight="bold" className="text-white" />
                ) : (
                  <Robot size={18} weight="duotone" className="text-[#0A1128]" />
                )}
              </div>
              <div className={`flex-1 max-w-[80%] ${
                message.role === 'user' ? 'text-right' : 'text-left'
              }`}>
                <div className={`inline-block rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-[#0A1128] text-white'
                    : 'bg-slate-100 text-[#1A202C]'
                }`}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-[#C5A059]">
                <Robot size={18} weight="duotone" className="text-[#0A1128]" />
              </div>
              <div className="bg-slate-100 rounded-lg px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-[#4A5568] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-[#4A5568] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-[#4A5568] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="ai-coach-input"
              placeholder="Ask about budgeting, retirement planning, tax strategies, and more..."
              className="flex-1 min-h-[60px] border-slate-300 bg-white resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            <Button
              type="submit"
              data-testid="ai-coach-send-button"
              disabled={loading || !input.trim()}
              className="bg-[#0A1128] hover:bg-[#152145] self-end"
            >
              <PaperPlaneRight size={20} weight="fill" />
            </Button>
          </form>
          <p className="text-xs text-[#4A5568] mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>

      {/* Bottom Disclaimer */}
      <div className="bg-slate-100 border border-slate-200 rounded-md p-4">
        <p className="text-xs text-[#4A5568] leading-relaxed">
          <strong className="text-[#1A202C]">Disclaimer:</strong> Information provided by this AI coach is for educational purposes only and should not be considered as personalized financial, investment, tax, or legal advice. Financial decisions should be made in consultation with qualified professionals who understand your unique circumstances. Past performance does not guarantee future results.
        </p>
      </div>
    </div>
  );
};

export default AIFinancialCoach;